import { useQuery } from "@tanstack/react-query";
import {
  BookMarked,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Clock,
  Database,
  Eye,
  FileText,
  FolderOpen,
  GraduationCap,
  Home,
  Terminal,
  Users,
  Bot,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getSettings } from "../../api/settingsApi";
import { useAuth } from "../../App";
import { ROLES } from "../../constant/roles";
import { cn } from "../../lib/utils";

export default function MainSidebar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(
    {},
  );

  if (!user) return null;

  // Get project ID from URL if we're on a project details page
  const projectDetailsMatch = location.pathname.match(
    /\/(view-project-details|project-details)\/([^\/]+)/,
  );
  const isOnProjectDetails = !!projectDetailsMatch;
  const projectId = projectDetailsMatch?.[2];

  // Detect if we're on view all projects page
  const isOnViewAllProjectsPage =
    location.pathname === "/view-all-project" ||
    location.pathname === "/allprojects";

  // Determine the source page based on location state or current path
  const getProjectDetailsSource = () => {
    const state = location.state as any;
    if (state?.from) return state.from;

    // Fallback logic based on user role and current navigation
    if (
      user.role === ROLES.INSTRUCTOR ||
      user.role === ROLES.TA ||
      user.role === ROLES.ADMIN
    ) {
      return "manage-projects"; // Default for instructors, TAs, and admins
    } else {
      return "dashboard"; // Default for students
    }
  };

  // Auto-expand relevant menus when on project details or view all projects
  useEffect(() => {
    if (isOnProjectDetails) {
      const source = getProjectDetailsSource();
      if (source === "manage-projects") {
        setExpandedMenus((prev) => ({ ...prev, "manage-projects": true }));
      } else if (source === "view-all-projects") {
        setExpandedMenus((prev) => ({ ...prev, dashboard: true }));
      }
    } else if (isOnViewAllProjectsPage) {
      // Auto-expand dashboard menu when on view all projects page
      setExpandedMenus((prev) => ({ ...prev, dashboard: true }));
    }
  }, [isOnProjectDetails, isOnViewAllProjectsPage, location.pathname]);

  const toggleMenu = (menuId: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  // Helper function to get the appropriate label based on mode query parameter
  const getProjectDetailsLabel = () => {
    const mode = searchParams.get("mode");
    return mode === "template"
      ? "View Template Details"
      : "View Project Details";
  };

  const getMenuItems = () => {
    const source = getProjectDetailsSource();

    if (user.role === ROLES.ADMIN) {
      const manageProjectsSubmenu: any[] = [];
      if (isOnProjectDetails && source === "manage-projects") {
        manageProjectsSubmenu.push({
          id: "view-project-details",
          label: getProjectDetailsLabel(),
          icon: Eye,
          path: `/view-project-details/${projectId}`,
          isActive: true,
        });
      }

      // Dashboard submenu for admin when on view all projects
      const dashboardSubmenu: any[] = [];
      if (isOnViewAllProjectsPage) {
        dashboardSubmenu.push({
          id: "view-all-projects",
          label: "View All Projects",
          icon: FolderOpen,
          path: "/view-all-project",
          isActive: true,
        });
      }

      return [
        {
          id: "dashboard",
          label: "Dashboard",
          icon: Home,
          path: "/",
          expandable: dashboardSubmenu.length > 0,
          submenu: dashboardSubmenu.length > 0 ? dashboardSubmenu : undefined,
        },
        {
          id: "user-management",
          label: "Users",
          icon: Users,
          path: "/user-management",
        },
        {
          id: "gradebook",
          label: "Gradebook",
          icon: GraduationCap,
          path: "/gradebook",
        },
        {
          id: "manage-projects",
          label: "Manage Projects",
          icon: FolderOpen,
          path: "/app/manage-projects",
          expandable: manageProjectsSubmenu.length > 0,
          submenu:
            manageProjectsSubmenu.length > 0
              ? manageProjectsSubmenu
              : undefined,
        },
        {
          id: "data-set",
          label: "Data Set",
          icon: Database,
          path: "/data-set",
        },
        {
          id: "sandbox-management",
          label: "Sandbox Management",
          icon: Terminal,
          expandable: true,
          submenu: [
            {
              id: "sandbox-management",
              label: "Overview",
              icon: Terminal,
              path: "/sandbox-management",
            },
            {
              id: "time-extensions",
              label: "Time Extensions",
              icon: Clock,
              path: "/time-extensions",
            },
          ],
        },
        // {
        //   id: "settings",
        //   label: "Settings",
        //   icon: Settings,
        //   path: "/settings",
        // },
        {
          id: "ai-assistant",
          label: "AI Tutor",
          icon: Bot,
          path: "/ai-chat",
        },
      ];
    } else if (user.role === ROLES.INSTRUCTOR || user.role === ROLES.TA) {
      const manageProjectsSubmenu: any[] = [];
      if (isOnProjectDetails && source === "manage-projects") {
        manageProjectsSubmenu.push({
          id: "view-project-details",
          label: getProjectDetailsLabel(),
          icon: Eye,
          path: `/view-project-details/${projectId}`,
          isActive: true,
        });
      }

      // Dashboard submenu for instructors when on view all projects
      const dashboardSubmenu: any[] = [];
      if (isOnViewAllProjectsPage) {
        dashboardSubmenu.push({
          id: "view-all-projects",
          label: "View All Projects",
          icon: FolderOpen,
          path: "/view-all-project",
          isActive: true,
        });
      }

      return {
        primary: [
          {
            id: "dashboard",
            label: "Dashboard",
            icon: Home,
            path: "/",
            expandable: dashboardSubmenu.length > 0,
            submenu: dashboardSubmenu.length > 0 ? dashboardSubmenu : undefined,
          },
          {
            id: "submissions",
            label: "Submissions",
            icon: FileText,
            path: "/submissions",
          },
        ],
        management: [
          {
            id: "manage-projects",
            label: "Manage Projects",
            icon: FolderOpen,
            path: "/app/manage-projects",
            expandable: manageProjectsSubmenu.length > 0,
            submenu:
              manageProjectsSubmenu.length > 0
                ? manageProjectsSubmenu
                : undefined,
          },
          {
            id: "data-set",
            label: "Data Set",
            icon: Database,
            path: "/data-set",
          },
          {
            id: "sandbox-management",
            label: "Sandbox Management",
            icon: Terminal,
            expandable: true,
            submenu: [
              {
                id: "sandbox-management",
                label: "Overview",
                icon: Terminal,
                path: "/sandbox-management",
              },
              {
                id: "time-extensions",
                label: "Time Extensions",
                icon: Clock,
                path: "/time-extensions",
              },
            ],
          },
          {
            id: "ai-assistant",
            label: "AI Tutor",
            icon: Bot,
            path: "/ai-chat",
          },
        ],
      };
    } else {
      // Student role
      const dashboardSubmenu: any[] = [];
      if (isOnProjectDetails && source === "dashboard") {
        dashboardSubmenu.push({
          id: "view-project-details",
          label: getProjectDetailsLabel(),
          icon: Eye,
          path: `/project-details/${projectId}`,
          isActive: true,
        });
      } else if (isOnProjectDetails && source === "view-all-projects") {
        dashboardSubmenu.push({
          id: "view-all-projects",
          label: "View All Projects",
          icon: FolderOpen,
          path: "/allprojects",
          expandable: true,
          submenu: [
            {
              id: "view-project-details",
              label: getProjectDetailsLabel(),
              icon: Eye,
              path: `/project-details/${projectId}`,
              isActive: true,
            },
          ],
        });
      } else if (isOnViewAllProjectsPage) {
        // Show "View All Projects" submenu when on the view all projects page
        dashboardSubmenu.push({
          id: "view-all-projects",
          label: "View All Projects",
          icon: FolderOpen,
          path: "/allprojects",
          isActive: true,
        });
      }

      return [
        {
          id: "dashboard",
          label: "Dashboard",
          icon: Home,
          path: "/",
          expandable: dashboardSubmenu.length > 0,
          submenu: dashboardSubmenu.length > 0 ? dashboardSubmenu : undefined,
        },
        {
          id: "my-courses",
          label: "My Courses",
          icon: BookMarked,
          path: "/my-courses",
        },
        {
          id: "practice-projects",
          label: "Practice Projects",
          icon: BookOpen,
          expandable: true,
          submenu: [
            {
              id: "browse-practice-projects",
              label: "Browse Projects",
              icon: Eye,
              path: "/practice-projects",
            },
            {
              id: "my-practice-projects",
              label: "My Projects",
              icon: FileText,
              path: "/my-practice-projects",
            },
          ],
        },
        {
          id: "gradebook",
          label: "Grades",
          icon: GraduationCap,
          path: "/gradebook",
        },
        {
          id: "ai-assistant",
          label: "AI Tutor",
          icon: Bot,
          path: "/ai-chat",
        },
      ];
    }
  };

  const adminToolsItems = [
    {
      id: "manage-projects",
      label: "Manage Projects",
      icon: FolderOpen,
      path: "/app/manage-projects",
    },
    {
      id: "data-set",
      label: "Data Set",
      icon: Database,
      path: "/data-set",
    },
    // {
    //   id: "sandbox-analytics",
    //   label: "Sandbox Analytics",
    //   icon: BarChart3,
    //   path: "/sandbox-analytics",
    // },
    // {
    //   id: "admin-reports",
    //   label: "Reports & Analytics",
    //   icon: BarChart3,
    //   path: "/admin-reports",
    // },
    // {
    //   id: "admin-audit",
    //   label: "Audit Logs",
    //   icon: Shield,
    //   path: "/admin-audit",
    // },
    // {
    //   id: "admin-settings",
    //   label: "System Settings",
    //   icon: Settings,
    //   path: "/admin-settings",
    // },
  ];

  const renderMenuItem = (item: any, isSubmenu = false) => {
    const isActive =
      location.pathname === item.path ||
      item.isActive ||
      location.pathname.startsWith(item.path + "/");
    const isExpanded = expandedMenus[item.id] || item.expandable;

    // Check if this menu item or any of its children are active
    const hasActiveChild =
      item.submenu &&
      item.submenu.some(
        (subItem: any) =>
          location.pathname === subItem.path ||
          subItem.isActive ||
          (subItem.submenu &&
            subItem.submenu.some(
              (nestedItem: any) =>
                location.pathname === nestedItem.path || nestedItem.isActive,
            )),
      );

    return (
      <li key={item.id} className={isSubmenu ? "ml-4" : ""}>
        <button
          onClick={() => {
            if (item.expandable && !item.path) {
              // Only toggle if expandable and has no path (pure expandable menu)
              toggleMenu(item.id);
            } else if (item.expandable && item.path) {
              // If both expandable and has path, navigate to path and toggle
              navigate(item.path);
              toggleMenu(item.id);
            } else if (item.path) {
              // Simple navigation
              console.log(item.path);
              navigate(item.path);
            }
          }}
          className={cn(
            "w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors cursor-pointer",
            isActive
              ? "bg-bits-blue text-white"
              : "text-gray-600 dark:text-[#c9d1d9] hover:bg-gray-100 dark:hover:bg-[#21262d]",
            !isActive && hasActiveChild && "bg-background dark:bg-[#0d1117] font-semibold",
            isSubmenu && "text-xs pl-6",
          )}
        >
          <item.icon className={`${isSubmenu ? "w-3 h-3" : "w-4 h-4"} mr-3`} />
          <span className="flex-1 text-left">{item.label}</span>
          {item.expandable && (
            <div className="ml-auto">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </div>
          )}
        </button>

        {item.expandable && item.submenu && isExpanded && (
          <ul className="space-y-1 mt-1">
            {item.submenu.map((subItem: any) => renderMenuItem(subItem, true))}
          </ul>
        )}
      </li>
    );
  };

  const menuItems: any = getMenuItems();
  const settingsQuery = useQuery({
    queryKey: ["/settings"],
    queryFn: getSettings,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 10 * 60 * 1000,
  });

  const sidebarColor = settingsQuery?.data?.data?.appearance?.navigationColor;

  return (
    // <div className="fixed left-0 z-40 bg-white border-gray-200 w-64 bg-sidebar border-r border-sidebar-border flex flex-col flex-shrink-0 z-30">
    <div
      className="fixed left-0 z-40 w-64 min-h-screen border-r border-gray-200 dark:border-[#30363d] flex flex-col transition-colors duration-200"
      style={{ backgroundColor: sidebarColor || (document.documentElement.classList.contains('dark') ? '#161b22' : 'white') }}
    >
      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 overflow-y-auto">
        {user.role === ROLES.INSTRUCTOR || user.role === ROLES.TA ? (
          // Instructor navigation with two sections
          <>
            {/* Primary Teaching Functions */}
            <div className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              Teaching & Grading
            </div>
            <ul className="space-y-1 mt-2">
              {menuItems.primary.map((item: any) => renderMenuItem(item))}
            </ul>

            {/* Management Functions */}
            <div className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-6">
              Course Management
            </div>
            <ul className="space-y-1 mt-2">
              {menuItems.management.map((item: any) => renderMenuItem(item))}
            </ul>
          </>
        ) : (
          // Regular navigation for admin and student roles
          <ul className="space-y-1">
            {(Array.isArray(menuItems) ? menuItems : []).map((item) =>
              renderMenuItem(item),
            )}
          </ul>
        )}
      </nav>
    </div>
  );
}
